import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { RootState } from "../../redux/store";
import {
  Search, MapPin, X, ChevronRight, ChevronLeft, AlertTriangle, Wallet, Languages,
  Car, HandCoins, Plug, Phone, Compass, Loader, Camera,
  CalendarDays, ListChecks, PiggyBank, Plus, Trash2, Check, LayoutGrid,
  UtensilsCrossed, Home, ShoppingBasket,
  Sparkles,
  MoreVertical,
  ArrowLeft,
  ArrowRight,
  AudioLines,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────
// PATH NOTE — same depth convention as before: adjust the "../../"
// prefix if this file doesn't sit two levels under src/.
// ─────────────────────────────────────────────────────────────
import heartGuideLogo from "../../assets/atlas/heartguide.png";
import {
  EMERGENCY_DB,
  RIDE_APPS,
  COUNTRY_META,
  getCountryCode,
  flagEmoji,
} from "../../GLOBAL/Guide";

/* ═══════════════════════════════════════════════════════════
   GOOGLE API KEY — used for on-device text detection (Vision API)
   and translation (Cloud Translation API) inside Translate Anything.
   Set REACT_APP_GOOGLE_MAPS_API_KEY (CRA) — or swap the line below
   for import.meta.env.VITE_GOOGLE_MAPS_API_KEY if you're on Vite.
   Both the Vision API and the Cloud Translation API must be enabled
   on that key in Google Cloud Console.
═══════════════════════════════════════════════════════════ */
const GOOGLE_MAPS_API_KEY: string =
  (typeof process !== "undefined" && (process as any).env?.REACT_APP_GOOGLE_MAPS_API_KEY) || "";

/* ═══════════════════════════════════════════════════════════
   TOKENS — white page, Heartlas red, chunky 3D buttons
═══════════════════════════════════════════════════════════ */
const HG = {
  bg: "#FFFFFF",
  panelSoft: "#FFF7F7",
  ink: "#1B0E06",
  inkMuted: "#7A6653",
  inkFaint: "#A6947F",
  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",
  gold: "#F2A93E",
  goldDark: "#D4900F",
  border: "#F1DCDC",
  divider: "#F6E8E8",
};
const GRAD_RED = `linear-gradient(135deg, ${HG.red} 0%, ${HG.redDark} 100%)`;
const GRAD_SPHERE = `linear-gradient(150deg, ${HG.gold} 0%, ${HG.red} 100%)`;

const CSS = `
  .hg-page *,.hg-page *::before,.hg-page *::after{box-sizing:border-box;}
  .hg-page{font-family:'Montserrat',-apple-system,BlinkMacSystemFont,sans-serif;background:${HG.bg};color:${HG.ink};min-height:100vh;}
  .hg-page ::-webkit-scrollbar{display:none;}
  .hg-page button{font-family:inherit;cursor:pointer;}
  .hg-page input,.hg-page select{font-family:inherit;}

  @keyframes hgPulse{0%,100%{opacity:1}50%{opacity:.35}}
  @keyframes hgSpin{to{transform:rotate(360deg);}}
  @keyframes hgHalo{0%{box-shadow:0 0 0 0 rgba(200,32,46,.32);}100%{box-shadow:0 0 0 18px rgba(200,32,46,0);}}
  @keyframes hgRise{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:none;}}

  .hg-btn3d{border:none;border-radius:14px;font-weight:800;color:#fff;background:${GRAD_RED};
    box-shadow:0 5px 0 ${HG.redDark}, 0 10px 22px rgba(200,32,46,.30);
    transition:transform .08s ease, box-shadow .08s ease;}
  .hg-btn3d:active{transform:translateY(4px);box-shadow:0 1px 0 ${HG.redDark}, 0 3px 10px rgba(200,32,46,.28);}
  .hg-btn3d:disabled{opacity:.5;cursor:not-allowed;}

  .hg-btn3d-soft{border:none;border-radius:14px;font-weight:800;color:${HG.redDark};background:#fff;
    border:1.5px solid ${HG.border};box-shadow:0 4px 0 ${HG.redSoft}, 0 8px 18px rgba(200,32,46,.08);
    transition:transform .08s ease, box-shadow .08s ease;}
  .hg-btn3d-soft:active{transform:translateY(3px);box-shadow:0 1px 0 ${HG.redSoft};}

  /* ── top tool rail (Translate Anything / Scheduler / Planner / Tracker) ── */
  .hg-rail{position:relative;background:#fff;border:1.5px solid ${HG.border};border-radius:22px;}
  .hg-rail-track{display:flex;gap:22px;overflow-x:auto;scrollbar-width:none;}
  .hg-rail-arrow{width:34px;height:34px;border-radius:50%;border:1.5px solid ${HG.border};background:#fff;
    display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(27,14,6,.1);flex-shrink:0;}

  .hg-sphere{position:relative;width:56px;height:56px;border-radius:50%;border:none;background:${GRAD_SPHERE};
    color:#fff;display:flex;align-items:center;justify-content:center;
    box-shadow:0 6px 0 ${HG.redDark}, 0 12px 24px rgba(200,32,46,.30);
    transition:transform .12s ease, box-shadow .12s ease;flex-shrink:0;}
  .hg-sphere.big{width:80px;height:80px;box-shadow:0 8px 0 ${HG.redDark}, 0 18px 34px rgba(200,32,46,.34);}
  .hg-sphere.big::after{content:"";position:absolute;inset:-6px;border-radius:50%;animation:hgHalo 2.4s ease-out infinite;pointer-events:none;}
  .hg-sphere:hover{transform:translateY(-4px);}
  .hg-sphere:active{transform:translateY(2px);}
  .hg-sphere-wrap{display:flex;flex-direction:column;align-items:center;gap:8px;background:none;border:none;flex-shrink:0;}
  .hg-sphere-label{font-size:10.5px;font-weight:800;color:${HG.ink};text-align:center;white-space:nowrap;}

  /* ── ONE rectangle card holding all topic orbs + dashed "tap for full details" footer ── */
  .hg-guidecard{background:#fff;border:1.5px solid ${HG.border};border-radius:22px;
    box-shadow:0 6px 0 ${HG.redSoft}, 0 14px 30px rgba(200,32,46,.08);overflow:hidden;}
  .hg-guidecard-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(88px,1fr));gap:6px 2px;}
  .hg-orb-wrap{display:flex;flex-direction:column;align-items:center;gap:6px;background:none;border:none;padding:6px 2px;
    border-radius:16px;transition:transform .12s ease, background .12s ease;}
  .hg-orb-wrap:hover{transform:translateY(-3px);background:${HG.panelSoft};}
  .hg-orb-wrap:active{transform:translateY(-1px);}
  .hg-orb{width:70px;height:70px;border-radius:50%;display:flex;align-items:center;justify-content:center;
    color:#fff;box-shadow:0 5px 0 rgba(0,0,0,.14), 0 12px 22px rgba(200,32,46,.24);}
  .hg-orb-label{font-size:10.5px;font-weight:800;color:${HG.ink};text-align:center;line-height:1.2;}

  .hg-guidecard-foot{width:100%;border-top:1.5px dashed ${HG.border};background:none;
    display:flex;align-items:center;justify-content:center;gap:8px;
    padding:16px 10px;font-size:12px;font-weight:800;color:${HG.red};text-transform:uppercase;letter-spacing:.06em;
    transition:background .12s ease;}
  .hg-guidecard-foot:hover{background:${HG.redSoft};}

  .hg-book-row{display:flex;justify-content:space-between;gap:14px;padding:10px 0;border-top:1px dashed ${HG.divider};}
  .hg-book-row:first-child{border-top:none;}
  .hg-book-k{font-size:10.5px;font-weight:800;color:${HG.inkFaint};text-transform:uppercase;letter-spacing:.06em;}
  .hg-book-v{font-size:13px;font-weight:800;color:${HG.ink};text-align:right;}

  .hg-allsection{margin-bottom:18px;}
  .hg-allsection:last-child{margin-bottom:0;}
  .hg-allsection-head{display:flex;align-items:center;gap:10px;margin-bottom:6px;}
  .hg-allsection-orb{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;
    color:#fff;flex-shrink:0;box-shadow:0 3px 0 rgba(0,0,0,.14);}
  .hg-allsection-title{font-size:13px;font-weight:900;color:${HG.ink};}

  /* ── scheduler sub-menu cards ── */
  .hg-schedcard{display:flex;align-items:center;gap:12px;width:100%;text-align:left;padding:14px 16px;
    border-radius:14px;border:1.5px solid ${HG.border};background:#fff;cursor:pointer;
    box-shadow:0 3px 0 ${HG.redSoft};transition:transform .1s ease, border-color .1s ease;margin-bottom:9px;}
  .hg-schedcard:last-child{margin-bottom:0;}
  .hg-schedcard:hover{border-color:${HG.red};transform:translateY(-2px);}
  .hg-schedcard:active{transform:translateY(0);}
  .hg-schedcard-ic{width:38px;height:38px;border-radius:12px;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0;}
  .hg-schedcard-label{flex:1;font-size:13.5px;font-weight:800;color:${HG.ink};}

  .hg-searchbar{display:flex;align-items:center;flex:1;min-width:0;background:#fff;
    border:1.5px solid ${HG.border};border-radius:999px;padding:5px 6px 5px 18px;gap:8px;}
  .hg-toolrow{display:flex;align-items:center;gap:10px;background:#fff;border:1.5px solid ${HG.border};
    border-radius:13px;padding:11px 14px;margin-bottom:8px;}
  .hg-checkbox{width:20px;height:20px;border-radius:6px;border:2px solid ${HG.border};display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .hg-checkbox.on{background:${HG.red};border-color:${HG.red};}

  @media (prefers-reduced-motion: reduce){.hg-page *{animation:none !important;transition:none !important;}}
`;

/* ═══════════════════════════════════════════════════════════
   COMBINED PER-COUNTRY GUIDE — stitched together from the
   shared tables, never re-declared here.
═══════════════════════════════════════════════════════════ */
interface CombinedGuide {
  code: string;
  name: string;
  emergency: string; police: string; ambulance: string; fire: string;
  rideApps: { name: string; url: string; color: string }[];
  currency: string; currencyCode: string; language: string; dialCode: string;
  drivingSide: "Right" | "Left"; plugType: string; tipping: string;
}

const buildGuide = (code: string, detectedName?: string | null): CombinedGuide => {
  const em = EMERGENCY_DB[code] ?? EMERGENCY_DB.DEFAULT;
  const rides = RIDE_APPS[code] ?? RIDE_APPS.DEFAULT;
  const meta = COUNTRY_META[code] ?? COUNTRY_META.DEFAULT;
  return {
    code,
    ...em,
    ...meta,
    rideApps: rides,
    name: detectedName || meta.name, // set last so it always wins
  };
};

type TopicId = "emergency" | "currency" | "language" | "rides" | "tipping" | "power" | "driving" | "dial";
interface Topic { id: TopicId; label: string; icon: React.ReactNode }

/* ── one line per topic — comment a whole line (incl. trailing comma) to hide it ── */
const TOPICS: Topic[] = [
  { id: "emergency", label: "Emergency",  icon: <AlertTriangle size={20} /> },
  { id: "currency",  label: "Currency",   icon: <Wallet size={20} /> },
  { id: "language",  label: "Language",   icon: <Languages size={20} /> },
  { id: "rides",     label: "Ride Apps",  icon: <Car size={20} /> },
  { id: "tipping",   label: "Tipping",    icon: <HandCoins size={20} /> },
  { id: "power",     label: "Power",      icon: <Plug size={20} /> },
  { id: "driving",   label: "Driving",    icon: <Compass size={20} /> },
  { id: "dial",      label: "Dial Code",  icon: <Phone size={20} /> },
];

const topicSpine = (id: TopicId) => {
  const idx = TOPICS.findIndex((t) => t.id === id);
  return idx % 2 === 0 ? HG.red : HG.gold;
};

const topicRows = (topic: TopicId, g: CombinedGuide): [string, string][] => {
  switch (topic) {
    case "emergency": return [["Emergency", g.emergency], ["Police", g.police], ["Ambulance", g.ambulance], ["Fire", g.fire]];
    case "currency": return [["Currency", g.currency], ["Code", g.currencyCode]];
    case "language": return [["Spoken language", g.language]];
    case "rides": return g.rideApps.map((a) => ["App", a.name]);
    case "tipping": return [["Custom", g.tipping]];
    case "power": return [["Plug type", g.plugType]];
    case "driving": return [["Side of the road", g.drivingSide]];
    case "dial": return [["Country dial code", g.dialCode]];
  }
};

/* ═══════════════════════════════════════════════════════════
   LANGUAGE LIST — fetched live from LibreTranslate's public,
   keyless API instead of a hardcoded array. Same shape as
   before ({ code, label }) so nothing downstream has to change.
═══════════════════════════════════════════════════════════ */
interface LangOption { code: string; label: string }

// tiny safety net only — used if the network call itself fails, not a substitute list
const FALLBACK_LANGUAGES: LangOption[] = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
];

const useTranslateLanguages = () => {
  const [languages, setLanguages] = useState<LangOption[]>(FALLBACK_LANGUAGES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch("https://libretranslate.com/languages")
      .then((res) => res.json())
      .then((data: { code: string; name: string }[]) => {
        if (cancelled || !Array.isArray(data)) return;
        setLanguages(data.map((l) => ({ code: l.code, label: l.name })));
      })
      .catch(() => {
        /* network hiccup — the fallback list above stays in place */
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { languages, loading };
};

// Maps a code to a readable name — checks the fetched list first, then falls back to the browser's Intl API
const langNameFrom = (languages: LangOption[], code: string): string => {
  const known = languages.find((l) => l.code === code);
  if (known) return known.label;
  try {
    return new Intl.DisplayNames(["en"], { type: "language" }).of(code) || code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
};
/* ═══════════════════════════════════════════════════════════
   RESPONSIVE PANEL — bottom sheet on mobile, floating rectangle
   modal on desktop. Used for topic details + toolbar panels + picker.
═══════════════════════════════════════════════════════════ */
const ResponsivePanel: React.FC<{
  open: boolean; onClose: () => void; title: string; icon?: React.ReactNode;
  spine?: string; isMobile: boolean; children: React.ReactNode;
}> = ({ open, onClose, title, icon, spine, isMobile, children }) => {
  if (!open) return null;
  const header = (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: isMobile ? "4px 20px 16px" : "22px 26px 16px" }}>
      {icon && (
        <span style={{ width: 44, height: 44, borderRadius: "50%", background: spine || HG.red, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 5px 0 rgba(0,0,0,.14), 0 10px 20px rgba(200,32,46,.22)" }}>
          {icon}
        </span>
      )}
      <span style={{ flex: 1, fontSize: 18, fontWeight: 900, color: HG.ink }}>{title}</span>
      <button onClick={onClose} aria-label="Close" className="hg-btn3d-soft" style={{ width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <X size={16} />
      </button>
    </div>
  );
  return createPortal(
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.5)", zIndex: 900, display: "flex", justifyContent: "center", alignItems: isMobile ? "flex-end" : "center" }}>
        {isMobile ? (
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxHeight: "82vh", overflowY: "auto", background: "#fff", borderRadius: "22px 22px 0 0", boxShadow: "0 -12px 40px rgba(27,14,6,.25)" }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: HG.border, margin: "12px auto 0" }} />
            {header}
            <div style={{ padding: "0 20px 28px" }}>{children}</div>
          </motion.div>
        ) : (
          <motion.div initial={{ scale: 0.94, opacity: 0, y: 14 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0, y: 14 }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }} onClick={(e) => e.stopPropagation()}
            style={{ width: "min(560px, calc(100vw - 48px))", maxHeight: "80vh", overflowY: "auto", background: "#fff", borderRadius: 22, boxShadow: "0 30px 80px rgba(27,14,6,.30)" }}>
            {header}
            <div style={{ padding: "0 26px 28px" }}>{children}</div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

/* ═══════════════════════════════════════════════════════════
   SINGLE TOPIC BODY — rows for one topic (used inside the
   individual-topic panel AND repeated inside the "all details" panel)
═══════════════════════════════════════════════════════════ */
const TopicRows: React.FC<{ topic: Topic; guide: CombinedGuide }> = ({ topic, guide }) => {
  const rows = topicRows(topic.id, guide);
  return (
    <div>
      {rows.map(([k, v], i) => (
        <div key={`${k}-${i}`} className="hg-book-row"><span className="hg-book-k">{k}</span><span className="hg-book-v">{v}</span></div>
      ))}
      {topic.id === "emergency" && (
        <a href={`tel:${guide.emergency}`} className="hg-btn3d" style={{ marginTop: 14, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "13px 0", fontSize: 12.5, textDecoration: "none" }}>
          <Phone size={14} /> Call {guide.emergency}
        </a>
      )}
    </div>
  );
};

/* All topics stacked in one panel — for the dashed "tap for details" footer */
const AllTopicsBody: React.FC<{ guide: CombinedGuide }> = ({ guide }) => (
  <div>
    {TOPICS.map((t) => (
      <div key={t.id} className="hg-allsection">
        <div className="hg-allsection-head">
          <span className="hg-allsection-orb" style={{ background: topicSpine(t.id) }}>{t.icon}</span>
          <span className="hg-allsection-title">{t.label}</span>
        </div>
        <TopicRows topic={t} guide={guide} />
      </div>
    ))}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   THE ONE GUIDE CARD — rectangle, responsive, holding every
   small 3D topic orb. Tap an orb → that topic's own data.
   Tap the dashed footer → every topic's data, all at once.
═══════════════════════════════════════════════════════════ */
const GuideCard: React.FC<{
  topics: Topic[]; onOpenTopic: (id: TopicId) => void; onOpenAll: () => void; isMobile: boolean;
}> = ({ topics, onOpenTopic, onOpenAll, isMobile }) => (
  <div className="hg-guidecard">
    <div style={{ padding: isMobile ? "18px 14px 12px" : "22px 20px 16px" }}>
      <div className="hg-guidecard-grid">
        {topics.map((t) => (
          <button key={t.id} className="hg-orb-wrap" onClick={() => onOpenTopic(t.id)}>
            <span className="hg-orb" style={{ background: topicSpine(t.id) }}>{t.icon}</span>
            <span className="hg-orb-label">{t.label}</span>
          </button>
        ))}
        {topics.length === 0 && (
          <p style={{ gridColumn: "1/-1", textAlign: "center", color: HG.inkFaint, fontSize: 12.5, padding: "10px 0" }}>No topic matches your search.</p>
        )}
      </div>
    </div>
    <button className="hg-guidecard-foot" onClick={onOpenAll}>
      <LayoutGrid size={13} /> Tap for full details <ChevronRight size={13} />
    </button>
  </div>
);
/* ═══════════════════════════════════════════════════════════
   FREE OCR + TRANSLATE STACK — no API key, no backend
   OCR:        Tesseract.js  (loaded from CDN, runs client-side)
   Lang ID:    franc-min     (pure JS, text-based detection)
   Translate:  MyMemory API  (free tier, anonymous, CORS-open)
═══════════════════════════════════════════════════════════ */

// Load Tesseract + franc once, lazily, from CDN
let tesseractPromise: Promise<any> | null = null;
const loadTesseract = () => {
  if (!tesseractPromise) {
    tesseractPromise = new Promise((resolve, reject) => {
      if ((window as any).Tesseract) return resolve((window as any).Tesseract);
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/tesseract.js/5.0.4/tesseract.min.js";
      script.onload = () => resolve((window as any).Tesseract);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return tesseractPromise;
};

const detectTextFromImageFree = async (
  dataUrl: string
): Promise<{ text: string }> => {
  const Tesseract = await loadTesseract();
  const { data } = await Tesseract.recognize(dataUrl, "eng"); // OCR script pack; swap/add langs as needed
  const text = (data.text || "").trim();
  return { text };
};

const translateTextFree = async (
  text: string,
  target: string
): Promise<{ translated: string; detectedLang: string | null }> => {
  const res = await fetch(
    `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=autodetect|${target}`
  );
  const json = await res.json();
  const usedPair: string | undefined = json?.matches?.[0]?.["source-lang"];
  return {
    translated: json?.responseData?.translatedText || "",
    detectedLang: usedPair || null,
  };
};

/* ═══════════════════════════════════════════════════════════
   TRANSLATE CAMERA — Lens-style layout, fully client-side
═══════════════════════════════════════════════════════════ */
const LIVE_SCAN_INTERVAL_MS = 2200; // OCR is heavier client-side; slightly slower cadence than before

const TranslateCamera: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const loopRef = useRef<number | null>(null);
  const busyRef = useRef(false);
  const lastTextRef = useRef("");

  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [paused, setPaused] = useState(false);

  const { languages: translateLanguages, loading: languagesLoading } = useTranslateLanguages();
  const [detectedLangCode, setDetectedLangCode] = useState<string | null>(null);
  const [targetLang, setTargetLang] = useState("en");
  const [translatedText, setTranslatedText] = useState("");
  const [mode, setMode] = useState<"search" | "translate" | "live" | "create">("translate");

  useEffect(() => {
    if (!open) return;
    setError(null); setReady(false); setPaused(false);
    setTranslatedText(""); setDetectedLangCode(null);
    lastTextRef.current = "";
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
        setReady(true);
      })
      .catch(() => setError("Camera access was blocked — enable it in your browser settings to translate signs and menus."));
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (loopRef.current) window.clearInterval(loopRef.current);
      loopRef.current = null;
    };
  }, [open]);

  const grabFrame = (): string | null => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return null;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.7);
  };

  const scanOnce = async (target: string) => {
    if (busyRef.current) return;
    const frame = grabFrame();
    if (!frame) return;
    busyRef.current = true;
    setScanning(true);
    try {
      const { text } = await detectTextFromImageFree(frame);
      if (text && text !== lastTextRef.current) {
        lastTextRef.current = text;
        setError(null);
        const { translated, detectedLang } = await translateTextFree(text, target);
        setDetectedLangCode(detectedLang);
        setTranslatedText(translated);
      }
    } catch {
      /* nothing readable in this frame — stay quiet and try the next one, like Lens does */
    }
    busyRef.current = false;
    setScanning(false);
  };

  useEffect(() => {
    if (!open || !ready || paused || mode !== "translate") return;
    scanOnce(targetLang);
    loopRef.current = window.setInterval(() => scanOnce(targetLang), LIVE_SCAN_INTERVAL_MS);
    return () => { if (loopRef.current) window.clearInterval(loopRef.current); loopRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ready, paused, targetLang, mode]);

  if (!open) return null;
  return createPortal(
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: "fixed", inset: 0, background: "#000", zIndex: 1000, display: "flex", flexDirection: "column" }}>

      {/* ── Top bar: back / flash / title / menu ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 6px" }}>
        <button onClick={onClose} className="hg-btn3d-soft" style={{ width: 36, height: 36, borderRadius: "50%" }}>
          <ArrowLeft size={17} color="#fff" />
        </button>
        <span style={{ color: "#fff", fontWeight: 800, fontSize: 15, display: "flex", alignItems: "center", gap: 7 }}>
          Translate {scanning && <Loader size={13} style={{ animation: "hgSpin .8s linear infinite" }} />}
        </span>
        <div style={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <MoreVertical size={17} color="#fff" />
        </div>
      </div>

      {/* ── Language pill row (Lens-style: source → target) ── */}
      <div style={{ display: "flex", justifyContent: "center", padding: "6px 0 10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#fff", borderRadius: 999, padding: "6px 8px" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6, background: "#E7E4FB", borderRadius: 999, padding: "8px 14px", fontSize: 13.5, fontWeight: 700, color: "#1a1a1a" }}>
            <Sparkles size={13} /> {detectedLangCode ? langNameFrom(translateLanguages, detectedLangCode) : "Detect language"}
          </span>
          <ArrowRight size={15} color="#444" />
          <select
            value={targetLang}
            onChange={(e) => { setTargetLang(e.target.value); lastTextRef.current = ""; }}
            disabled={languagesLoading}
            style={{ background: "#E7E4FB", borderRadius: 999, padding: "8px 14px", fontSize: 13.5, fontWeight: 700, color: "#1a1a1a", border: "none" }}
          >
            {translateLanguages.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>
      </div>

      {/* ── Camera feed ── */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        <video ref={videoRef} muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />

        {!ready && !error && (
          <Loader size={26} color="#fff" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", animation: "hgSpin .8s linear infinite" }} />
        )}

        {translatedText && !error && (
          <div style={{ position: "absolute", left: 14, right: 14, bottom: 14, background: "rgba(0,0,0,.72)", borderRadius: 14, padding: "12px 14px", backdropFilter: "blur(4px)" }}>
            <p style={{ fontSize: 14.5, fontWeight: 700, color: "#fff", margin: 0, lineHeight: 1.4 }}>{translatedText}</p>
          </div>
        )}

        {error && (
          <div style={{ position: "absolute", left: 14, right: 14, bottom: 14, background: "rgba(0,0,0,.72)", borderRadius: 14, padding: "12px 14px" }}>
            <p style={{ fontSize: 12.5, color: "#fff", fontWeight: 700, margin: 0 }}>{error}</p>
          </div>
        )}
      </div>

      {/* ── Bottom capture row (thumbnail + shutter, Lens-style) ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 40, padding: "18px 0 10px" }}>
        <div style={{ width: 46, height: 46, borderRadius: "50%", border: "2px solid #fff", background: "rgba(255,255,255,.15)" }} />
        <button onClick={() => setPaused((p) => !p)} style={{ width: 68, height: 68, borderRadius: "50%", background: "#fff", border: "4px solid rgba(255,255,255,.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Languages size={26} color="#1a1a1a" />
        </button>
        <div style={{ width: 46, height: 46 }} />
      </div>

      {/* ── Bottom tab bar (Search / Translate / Live / Create) ── */}
      <div style={{ display: "flex", justifyContent: "center", paddingBottom: 26 }}>
        <div style={{ display: "flex", background: "rgba(20,20,20,.9)", borderRadius: 999, padding: 4, gap: 2 }}>
          {[
            { key: "search", label: "Search", icon: Search },
            { key: "translate", label: "Translate", icon: Languages },
            { key: "live", label: "Live", icon: AudioLines },
            { key: "create", label: "Create", icon: Sparkles },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setMode(key as any)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                padding: "8px 16px", borderRadius: 999, border: "none",
                background: mode === key ? "#000" : "transparent",
                color: "#fff", fontSize: 11, fontWeight: 700,
              }}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>
      </div>
    </motion.div>,
    document.body
  );
};




const PlannerPanel: React.FC = () => {
  const [items, setItems] = useState<{ id: string; text: string; done: boolean }[]>([
    { id: "p1", text: "Passport / ID", done: false },
    { id: "p2", text: "Power adapter", done: false },
    { id: "p3", text: "Travel insurance", done: false },
  ]);
  const [text, setText] = useState("");
  const add = () => { if (!text.trim()) return; setItems((p) => [...p, { id: `${Date.now()}`, text, done: false }]); setText(""); };
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add to the list…" onKeyDown={(e) => e.key === "Enter" && add()}
          style={{ flex: 1, border: `1.5px solid ${HG.border}`, borderRadius: 10, padding: "10px 12px", fontSize: 13 }} />
        <button className="hg-btn3d" onClick={add} style={{ width: 42, display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={16} /></button>
      </div>
      {items.map((i) => (
        <div key={i.id} className="hg-toolrow">
          <button className={`hg-checkbox${i.done ? " on" : ""}`} onClick={() => setItems((p) => p.map((x) => x.id === i.id ? { ...x, done: !x.done } : x))}>
            {i.done && <Check size={12} color="#fff" />}
          </button>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 700, textDecoration: i.done ? "line-through" : "none", color: i.done ? HG.inkFaint : HG.ink }}>{i.text}</span>
          <button onClick={() => setItems((p) => p.filter((x) => x.id !== i.id))} style={{ background: "none", border: "none", color: HG.inkFaint }}><Trash2 size={15} /></button>
        </div>
      ))}
    </div>
  );
};

const TrackerPanel: React.FC<{ currencyCode: string }> = ({ currencyCode }) => {
  const [items, setItems] = useState<{ id: string; label: string; amount: number }[]>([]);
  const [label, setLabel] = useState(""); const [amount, setAmount] = useState("");
  const add = () => { const n = parseFloat(amount); if (!label.trim() || !n) return; setItems((p) => [...p, { id: `${Date.now()}`, label, amount: n }]); setLabel(""); setAmount(""); };
  const total = items.reduce((s, i) => s + i.amount, 0);
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="What was it for?"
          style={{ flex: 1, border: `1.5px solid ${HG.border}`, borderRadius: 10, padding: "10px 12px", fontSize: 13 }} />
        <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" inputMode="decimal" onKeyDown={(e) => e.key === "Enter" && add()}
          style={{ width: 90, border: `1.5px solid ${HG.border}`, borderRadius: 10, padding: "10px 12px", fontSize: 13 }} />
        <button className="hg-btn3d" onClick={add} style={{ width: 42, display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={16} /></button>
      </div>
      {items.map((i) => (
        <div key={i.id} className="hg-toolrow">
          <span style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>{i.label}</span>
          <span style={{ fontSize: 13, fontWeight: 800, color: HG.red }}>{i.amount.toFixed(2)} {currencyCode !== "—" ? currencyCode : ""}</span>
          <button onClick={() => setItems((p) => p.filter((x) => x.id !== i.id))} style={{ background: "none", border: "none", color: HG.inkFaint }}><Trash2 size={15} /></button>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", padding: "14px 4px 0", borderTop: `1px solid ${HG.divider}`, marginTop: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: HG.inkFaint, textTransform: "uppercase", letterSpacing: ".06em" }}>Total spent</span>
        <span style={{ fontSize: 16, fontWeight: 900, color: HG.ink }}>{total.toFixed(2)} {currencyCode !== "—" ? currencyCode : ""}</span>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const HeartGuide: React.FC = () => {
  const navigate = useNavigate();
  const authUser = useSelector((s: RootState) => (s as any).auth?.user);
  const userId = authUser?.userId ?? "";

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 820);
  const [searchQuery, setSearchQuery] = useState("");

  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [countryName, setCountryName] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationDenied, setLocationDenied] = useState(false);

  const [openTopicId, setOpenTopicId] = useState<TopicId | null>(null);
  const [allOpen, setAllOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [schedulerMenuOpen, setSchedulerMenuOpen] = useState(false);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [trackerOpen, setTrackerOpen] = useState(false);

  const railRef = useRef<HTMLDivElement | null>(null);
  const scrollRail = (dir: 1 | -1) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: "smooth" });
  };

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 820);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* detect the device's country via Google Maps Geocoder — same
     pattern as ShortChecking.tsx, now sourced from Guide.tsx */
  useEffect(() => {
    if (!navigator.geolocation) { setLocationDenied(true); setLocationLoading(false); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const geo = await getCountryCode(pos.coords.latitude, pos.coords.longitude);
        setCountryCode(geo.code); setCountryName(geo.name); setCity(geo.city);
        setLocationLoading(false);
      },
      () => { setLocationDenied(true); setLocationLoading(false); }
    );
  }, []);

  const guide = useMemo(() => buildGuide(countryCode ?? "DEFAULT", countryName), [countryCode, countryName]);
  const resolvedFlag = countryCode && countryCode !== "DEFAULT" ? flagEmoji(countryCode) : "🌍";

  const filteredTopics = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return q ? TOPICS.filter((t) => t.label.toLowerCase().includes(q)) : TOPICS;
  }, [searchQuery]);

  const pickerCountries = useMemo(
    () => Object.keys(COUNTRY_META).filter((c) => c !== "DEFAULT"),
    []
  );

  const pickCountry = (code: string) => {
    setCountryCode(code); setCountryName(COUNTRY_META[code]?.name ?? null);
    setLocationDenied(false); setPickerOpen(false);
  };

  const activeTopic = TOPICS.find((t) => t.id === openTopicId) ?? null;

  /* ─────────────────────────────────────────────────────────
     TOP TOOL RAIL — one line per button. Comment a whole line
     (including its trailing comma) to hide that button.
     ───────────────────────────────────────────────────────── */
  const TOOLS = [
    { id: "translate", label: "Translate Anything", icon: <Camera size={28} />, big: true, onClick: () => setCameraOpen(true) }, // comment this line to hide Translate Anything
    { id: "scheduler",  label: "Scheduler",          icon: <CalendarDays size={24} />,       onClick: () => setSchedulerMenuOpen(true) }, // comment this line to hide Scheduler
    { id: "planner",    label: "Planner",            icon: <ListChecks size={24} />,          onClick: () => setPlannerOpen(true) }, // comment this line to hide Planner
    { id: "tracker",    label: "Tracker",            icon: <PiggyBank size={24} />,           onClick: () => setTrackerOpen(true) }, // comment this line to hide Tracker
  ];

  /* ─────────────────────────────────────────────────────────
     SCHEDULER SUB-MENU — one line per option. Comment a whole
     line (including its trailing comma) to hide that option.
     ───────────────────────────────────────────────────────── */
  const SCHEDULE_OPTIONS = [
    { id: "ride",      label: "Schedule a Ride",             icon: <Car size={18} />,            spine: HG.red,  onClick: () => { setSchedulerMenuOpen(false); navigate(`/RideScheduler/${userId}`); } }, // comment this line to hide "Schedule a Ride"
    { id: "food",      label: "Schedule Food",                icon: <UtensilsCrossed size={18} />, spine: HG.gold, onClick: () => { setSchedulerMenuOpen(false); navigate(`/FoodScheduler/${userId}`); } }, // comment this line to hide "Schedule Food"
    { id: "vacation",  label: "Schedule A Vacation Rental",    icon: <Home size={18} />,           spine: HG.red,  onClick: () => { setSchedulerMenuOpen(false); navigate(`/VacationRentalScheduler/${userId}`); } }, // comment this line to hide "Schedule A Vacation Rental"
    { id: "grocery",   label: "Schedule Grocery",              icon: <ShoppingBasket size={18} />, spine: HG.gold, onClick: () => { setSchedulerMenuOpen(false); navigate(`/GroceryScheduler/${userId}`); } }, // comment this line to hide "Schedule Grocery"
  ];

  const sphereBtn = (opts: { label: string; icon: React.ReactNode; big?: boolean; onClick: () => void }) => (
    <button className="hg-sphere-wrap" onClick={opts.onClick}>
      <span className={`hg-sphere${opts.big ? " big" : ""}`}>{opts.icon}</span>
      <span className="hg-sphere-label">{opts.label}</span>
    </button>
  );

  if (locationLoading) {
    return (
      <div className="hg-page" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <style>{CSS}</style>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", margin: "0 auto 14px", background: GRAD_RED, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 10px 26px rgba(200,32,46,.35)" }}>
            <Loader size={22} color="#fff" style={{ animation: "hgSpin .8s linear infinite" }} />
          </div>
          <p style={{ color: HG.inkMuted, fontWeight: 700, fontSize: 13 }}>Finding out where you are…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hg-page">
      <style>{CSS}</style>

      {/* ── header ── */}
      <header style={{ borderBottom: `1px solid ${HG.divider}`, background: "#fff", position: "sticky", top: 0, zIndex: 30 }}>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 12 : 20, padding: isMobile ? "12px 14px" : "14px 26px", flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, flexShrink: 0 }}>
            <img src={heartGuideLogo} alt="HeartGuide" style={{ width: isMobile ? 30 : 36, height: isMobile ? 30 : 36, objectFit: "contain" }}
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
              <span style={{ fontWeight: 900, fontSize: isMobile ? 15 : 17, color: HG.red, letterSpacing: "-.02em" }}>heartguide</span>
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".3em", color: HG.inkMuted, marginTop: 2 }}>TRAVEL SMART</span>
            </div>
          </div>

          <button onClick={() => setPickerOpen(true)} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", flexShrink: 0, textAlign: "left" }}>
            <MapPin size={17} color={HG.red} />
            <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: HG.inkFaint }}>You're in</span>
              <span style={{ fontSize: 12.5, fontWeight: 900, color: HG.ink }}>{resolvedFlag} {city ? `${city}, ` : ""}{guide.name}</span>
            </span>
          </button>

          <div className="hg-searchbar" style={{ order: isMobile ? 10 : 0, flexBasis: isMobile ? "100%" : undefined }}>
            <Search size={15} color={HG.inkFaint} />
            <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search a topic…"
              style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontSize: 13.5, color: HG.ink }} />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} aria-label="Clear" style={{ background: HG.border, border: "none", borderRadius: 999, width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={11} color={HG.inkMuted} />
              </button>
            )}
            <button className="hg-btn3d" aria-label="Search" style={{ width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Search size={15} />
            </button>
          </div>
        </div>
      </header>

      {/* ── TOP TOOL RAIL — driven by the TOOLS array above ── */}
      <div style={{ margin: isMobile ? "16px 14px 0" : "22px 26px 0" }}>
        <p style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".18em", color: HG.inkFaint, margin: "0 0 12px 4px" }}>QUICK TOOLS</p>
        <div className="hg-rail" style={{ padding: isMobile ? "16px 14px" : "18px 20px" }}>
          <div ref={railRef} className="hg-rail-track">
            {TOOLS.map((tool) => sphereBtn(tool))}
          </div>
          {!isMobile && (
            <>
              <button className="hg-rail-arrow" onClick={() => scrollRail(-1)} aria-label="Scroll left"
                style={{ position: "absolute", left: -17, top: "50%", transform: "translateY(-50%)" }}>
                <ChevronLeft size={16} color={HG.ink} />
              </button>
              <button className="hg-rail-arrow" onClick={() => scrollRail(1)} aria-label="Scroll right"
                style={{ position: "absolute", right: -17, top: "50%", transform: "translateY(-50%)" }}>
                <ChevronRight size={16} color={HG.ink} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── hero ── */}
      <div style={{ margin: isMobile ? "16px 14px 6px" : "22px 26px 8px", background: HG.panelSoft, border: `1px solid ${HG.border}`, borderRadius: 20, padding: isMobile ? "20px 18px" : "28px 32px" }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? 21 : 27, fontWeight: 900, letterSpacing: "-.01em" }}>Landed somewhere new? {resolvedFlag}</h1>
        <p style={{ margin: "8px 0 0", fontSize: 13, color: HG.inkMuted, lineHeight: 1.6, maxWidth: 520 }}>
          Tap any icon for a quick answer, or open the full rundown for {guide.name} below.
        </p>
        {locationDenied && (
          <button className="hg-btn3d" onClick={() => setPickerOpen(true)} style={{ marginTop: 14, padding: "10px 20px", fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 8 }}>
            <MapPin size={14} /> Choose your country
          </button>
        )}
      </div>

      {/* ── THE ONE GUIDE CARD ── */}
      <div style={{ margin: isMobile ? "10px 14px 24px" : "10px 26px 30px", maxWidth: 640 }}>
        <GuideCard
          topics={filteredTopics}
          isMobile={isMobile}
          onOpenTopic={(id) => setOpenTopicId(id)}
          onOpenAll={() => setAllOpen(true)}
        />
      </div>

      {/* ── COMMENT THIS BLOCK OUT (with the "translate" line in TOOLS above) TO HIDE Translate Anything ── */}
      <TranslateCamera open={cameraOpen} onClose={() => setCameraOpen(false)} />

      {/* ── scheduler sub-menu — driven by the SCHEDULE_OPTIONS array above ── */}
      <ResponsivePanel open={schedulerMenuOpen} onClose={() => setSchedulerMenuOpen(false)} title="Schedule Something" icon={<CalendarDays size={20} />} spine={HG.red} isMobile={isMobile}>
        <p style={{ fontSize: 12.5, color: HG.inkMuted, marginBottom: 14 }}>What would you like to plan ahead?</p>
        {SCHEDULE_OPTIONS.map((opt) => (
          <button key={opt.id} className="hg-schedcard" onClick={opt.onClick}>
            <span className="hg-schedcard-ic" style={{ background: opt.spine }}>{opt.icon}</span>
            <span className="hg-schedcard-label">{opt.label}</span>
            <ChevronRight size={16} color={HG.inkFaint} />
          </button>
        ))}
      </ResponsivePanel>

      <ResponsivePanel open={plannerOpen} onClose={() => setPlannerOpen(false)} title="Planner" icon={<ListChecks size={20} />} spine={HG.red} isMobile={isMobile}><PlannerPanel /></ResponsivePanel>
      <ResponsivePanel open={trackerOpen} onClose={() => setTrackerOpen(false)} title="Tracker" icon={<PiggyBank size={20} />} spine={HG.red} isMobile={isMobile}><TrackerPanel currencyCode={guide.currencyCode} /></ResponsivePanel>

      {/* ── individual topic — clicking one orb ── */}
      <ResponsivePanel open={!!activeTopic} onClose={() => setOpenTopicId(null)} title={activeTopic?.label ?? ""} icon={activeTopic?.icon} spine={activeTopic ? topicSpine(activeTopic.id) : HG.red} isMobile={isMobile}>
        {activeTopic && <TopicRows topic={activeTopic} guide={guide} />}
      </ResponsivePanel>

      {/* ── full details — the dashed footer ── */}
      <ResponsivePanel open={allOpen} onClose={() => setAllOpen(false)} title={`${guide.name} — Full Details`} icon={<LayoutGrid size={20} />} spine={HG.red} isMobile={isMobile}>
        <AllTopicsBody guide={guide} />
      </ResponsivePanel>

      <ResponsivePanel open={pickerOpen} onClose={() => setPickerOpen(false)} title="Choose your country" icon={<MapPin size={20} />} spine={HG.red} isMobile={isMobile}>
        <p style={{ fontSize: 12.5, color: HG.inkMuted, marginBottom: 14 }}>We'll only show details for the one you pick.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
          {pickerCountries.map((code) => {
            const on = countryCode === code;
            return (
              <button key={code} onClick={() => pickCountry(code)}
                style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", padding: "12px 14px", borderRadius: 13, border: `1.5px solid ${on ? HG.red : HG.border}`, background: on ? HG.redSoft : "#fff", cursor: "pointer" }}>
                <span style={{ fontSize: 20 }}>{flagEmoji(code)}</span>
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: 800, color: HG.ink }}>{COUNTRY_META[code].name}</span>
                {on && <ChevronRight size={15} color={HG.red} />}
              </button>
            );
          })}
        </div>
      </ResponsivePanel>
    </div>
  );
};

export default HeartGuide;